import { Fragment, useMemo, useState } from 'react';
import styles from './TargetsPage.module.css';
import { useSaveTargets, useTargets, type TargetRow } from './useTargets';
import { ReportShell } from '@/features/reports/ReportShell';
import { Card, ErrorBox, State, inr } from '@/features/reports/Panels';
import { loadActiveProfile } from '@/lib/auth';
import { DEFAULT_RANGE, type DateRange } from '@/lib/dateRange';
import type { Route } from '@/lib/route';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09" -> "Sep 2026". Left alone if it is not a month key. */
function monthLabel(m: string): string {
  const [y, mo] = m.split('-');
  const i = Number(mo) - 1;
  return y && MONTHS[i] ? `${MONTHS[i]} ${y}` : m;
}

const rowKey = (r: { subline: string; month: string }): string => `${r.subline} ${r.month}`;

/**
 * The stored value as the box should show it: a whole number.
 *
 * A plan is a commitment in whole units and whole rupees, but the seed rolled
 * these cells up from a 210-row SKU x city-band plan and left float residue
 * behind — the GMV box for one month read 3964951.50999992. The store now
 * rounds at both ends too; this keeps the editor right against a server that
 * has not been redeployed yet, and it is the SAME string the dirty check and
 * the save payload use, so a freshly loaded row is never marked as edited.
 */
const shown = (n: number): string => String(Number.isFinite(n) ? Math.round(n) : 0);

/**
 * A number the user is part-way through typing.
 *
 * Edits are held as STRINGS, not numbers. Parsing on every keystroke turns an
 * emptied field into NaN and an in-progress "1." into 1, so the input fights
 * the person typing in it. The string is the draft; a number is only derived
 * at save time.
 */
type Draft = Record<string, string>;

const isValid = (raw: string): boolean => {
  if (raw.trim() === '') return false;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0;
};

export function TargetsPage({
  route, onNavigate, onSignOut,
}: { route: Route; onNavigate: (s: string, p: string) => void; onSignOut?: () => void }) {
  // The shell wants a range; targets are not windowed, so this is inert here
  // and exists only so the page wears the same chrome as everything else.
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const customer = loadActiveProfile() ?? '';
  const q = useTargets(customer);
  const save = useSaveTargets(customer);

  const [draft, setDraft] = useState<Draft>({});
  const [note, setNote] = useState('');

  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);

  const valueOf = (r: TargetRow, field: 'units' | 'gmv'): string =>
    draft[`${rowKey(r)} ${field}`] ?? shown(r[field]);

  const setValue = (r: TargetRow, field: 'units' | 'gmv', raw: string): void => {
    setDraft((p) => ({ ...p, [`${rowKey(r)} ${field}`]: raw }));
  };

  const isDirty = (r: TargetRow, field: 'units' | 'gmv'): boolean => {
    const k = `${rowKey(r)} ${field}`;
    return k in draft && draft[k] !== shown(r[field]);
  };

  const edited = rows.filter((r) => isDirty(r, 'units') || isDirty(r, 'gmv'));
  const invalid = rows.filter(
    (r) => !isValid(valueOf(r, 'units')) || !isValid(valueOf(r, 'gmv')),
  );
  const canSave = edited.length > 0 && invalid.length === 0 && !save.isPending;

  const onSave = (): void => {
    save.mutate(
      {
        note: note.trim(),
        rows: rows.map((r) => ({
          subline: r.subline,
          month: r.month,
          units: Math.round(Number(valueOf(r, 'units'))),
          gmv: Math.round(Number(valueOf(r, 'gmv'))),
        })),
      },
      { onSuccess: () => { setDraft({}); setNote(''); } },
    );
  };

  // Grouped for display only. The payload stays a flat list, which is the
  // shape the server stores and the shape a diff is easiest to read in.
  const bySubline = useMemo(() => {
    const out = new Map<string, TargetRow[]>();
    for (const r of rows) {
      const list = out.get(r.subline) ?? [];
      list.push(r);
      out.set(r.subline, list);
    }
    return [...out.entries()].map(([subline, list]) => ({
      subline,
      list: [...list].sort((a, b) => a.month.localeCompare(b.month)),
    }));
  }, [rows]);

  const active = q.data?.version;

  return (
    <ReportShell
      title="Settings — Targets"
      route={{ section: 'settings', page: route.page || 'targets' }}
      onNavigate={onNavigate}
      range={range}
      onRangeChange={setRange}
      {...(onSignOut ? { onSignOut } : {})}
    >
      {q.isLoading && <State>Loading targets…</State>}
      {q.isError && <ErrorBox>{q.error.message}</ErrorBox>}

      {/*
        No plan yet is a normal state, not a failure. Without this the page
        rendered nothing at all for a brand that has never been seeded, which
        reads as a broken screen rather than an empty one.
      */}
      {q.data && !active && (
        <State>
          No plan has been saved for this brand yet. Seed one with
          {' '}<code>tools/seed-targets.ts</code>{' '}in the API, and it becomes editable here.
        </State>
      )}

      {q.data && active && (
        <div className={styles.wrap}>
          <Card title="Monthly plan" note={`${rows.length} rows · ${bySubline.length} sub-lines`}>
            <div className={styles.meta}>
              <span>Active version <b>v{active.version}</b></span>
              <span>set by <b>{active.created_by || 'unknown'}</b></span>
              <span>on <b>{active.created_at ? active.created_at.slice(0, 16).replace('T', ' ') : '—'}</b></span>
              {active.note ? <span>“{active.note}”</span> : null}
            </div>

            <table className={styles.grid}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th className={styles.num}>Units</th>
                  <th className={styles.num}>GMV (₹, MRP basis)</th>
                  <th className={styles.num}>Implied ₹/unit</th>
                </tr>
              </thead>
              <tbody>
                {bySubline.map(({ subline, list }) => (
                  <Fragment key={subline}>
                    <tr className={styles.subhead}>
                      <td colSpan={4}>{subline}</td>
                    </tr>
                    {list.map((r) => {
                      const u = valueOf(r, 'units');
                      const g = valueOf(r, 'gmv');
                      const per =
                        isValid(u) && isValid(g) && Number(u) > 0
                          ? `₹${(Number(g) / Number(u)).toFixed(2)}`
                          : '—';
                      return (
                        <tr key={rowKey(r)}>
                          <td>{monthLabel(r.month)}</td>
                          <td className={styles.num}>
                            <input
                              className={`${styles.input} ${isDirty(r, 'units') ? styles.dirty : ''} ${isValid(u) ? '' : styles.bad}`}
                              inputMode="decimal"
                              aria-label={`${subline} ${r.month} units`}
                              value={u}
                              onChange={(e) => { setValue(r, 'units', e.target.value); }}
                            />
                          </td>
                          <td className={styles.num}>
                            <input
                              className={`${styles.input} ${isDirty(r, 'gmv') ? styles.dirty : ''} ${isValid(g) ? '' : styles.bad}`}
                              inputMode="decimal"
                              aria-label={`${subline} ${r.month} GMV`}
                              value={g}
                              onChange={(e) => { setValue(r, 'gmv', e.target.value); }}
                            />
                            <div className={styles.derived}>
                              {isValid(g) ? inr(Number(g)) : 'not a number'}
                            </div>
                          </td>
                          <td className={`${styles.num} ${styles.derived}`}>{per}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>

            <div className={styles.foot}>
              <input
                className={styles.note}
                placeholder="What changed, and why (saved with the version)"
                value={note}
                onChange={(e) => { setNote(e.target.value); }}
              />
              <div className={styles.actions}>
                {invalid.length > 0 && (
                  <span className={`${styles.msg} ${styles.err}`}>
                    {invalid.length} cell{invalid.length === 1 ? '' : 's'} not a number ≥ 0
                  </span>
                )}
                {invalid.length === 0 && edited.length > 0 && (
                  <span className={styles.msg}>
                    {edited.length} row{edited.length === 1 ? '' : 's'} changed
                  </span>
                )}
                {save.isError && (
                  <span className={`${styles.msg} ${styles.err}`}>{save.error.message}</span>
                )}
                {save.isSuccess && edited.length === 0 && (
                  <span className={`${styles.msg} ${styles.ok}`}>Saved as v{active.version}</span>
                )}
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  disabled={edited.length === 0 || save.isPending}
                  onClick={() => { setDraft({}); }}
                >
                  Discard
                </button>
                <button type="button" className={styles.btn} disabled={!canSave} onClick={onSave}>
                  {save.isPending ? 'Saving…' : 'Save as new version'}
                </button>
              </div>
            </div>

            <div className={styles.basis}>
              <b>GMV targets are on the MRP basis</b>, as the plan states. Achievement on the Plan
              &amp; forecast page is measured against portal actuals on that same basis — never
              against the selling-price figure, which runs about 15% lower and would make every
              month look short.
            </div>
          </Card>

          <Card title="Version history" note="Editing never overwrites — each save keeps the one before it">
            <div className={styles.history}>
              <table>
                <thead>
                  <tr><th>Version</th><th>When</th><th>By</th><th>Note</th></tr>
                </thead>
                <tbody>
                  {q.data.versions.map((v) => (
                    <tr key={v.version}>
                      <td>v{v.version}{v.version === active.version ? ' (active)' : ''}</td>
                      <td>{v.created_at ? v.created_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                      <td>{v.created_by || '—'}</td>
                      <td>{v.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </ReportShell>
  );
}
